import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { analyticsService } from '../services/analytics.service';
import { tenantsService } from '../services/tenants.service';
import { useSelectedColoc } from '../hooks/useSelectedColoc';
import { TenantDetail } from '../types/tenant';
import ExpensesChart from '../components/ExpensesChart';
import ConsommationEnergie from '../components/ConsommationEnergie';
import '../styles/TenantDashboardPage.css';

interface TenantDashboardPageProps {
  /** Aperçu du propriétaire : la colocation et l'entrée viennent de ce dossier. */
  apercu?: TenantDetail;
}

const TenantDashboardPage: React.FC<TenantDashboardPageProps> = ({ apercu }) => {
  const colocChoisie = useSelectedColoc();
  // Mémorisé : un objet neuf à chaque rendu relancerait le chargement sans fin.
  const selectedColoc = useMemo(
    () => (apercu
      ? (apercu.colocId ? { id: apercu.colocId, name: apercu.colocName ?? '' } : null)
      : colocChoisie),
    [apercu, colocChoisie],
  );
  const [expenseData, setExpenseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const navigate = useNavigate();
  // Son entrée dans les lieux : elle découpe les deux graphiques. Un échec de
  // chargement laisse `null`, donc l'année entière — mieux vaut un mois de trop
  // qu'un écran vide, et la mention ci-dessous dit déjà que rien n'est un décompte.
  const [entree, setEntree] = useState<Date | null>(
    apercu?.leaseStartDate ? new Date(apercu.leaseStartDate) : null,
  );
  // Colocation « consommations seulement » : aucun montant (décision de Julien du
  // 2026-10-11). `null` tant que le dossier n'est pas lu — on n'affiche alors rien,
  // plutôt que des euros qui disparaîtraient une seconde plus tard.
  const [consoSeulement, setConsoSeulement] = useState<boolean | null>(
    apercu ? apercu.showOnlyConsumption : null,
  );

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/signin');
    }
  }, [navigate]);

  useEffect(() => {
    if (apercu) return undefined;
    let vivant = true;
    tenantsService
      .getMyDossier()
      .then((d) => {
        if (!vivant) return;
        if (d.leaseStartDate) setEntree(new Date(d.leaseStartDate));
        setConsoSeulement(d.showOnlyConsumption);
      })
      // Le serveur refuse de toute façon les montants si le réglage est actif.
      .catch(() => vivant && setConsoSeulement(false));
    return () => {
      vivant = false;
    };
  }, [apercu]);

  useEffect(() => {
    if (!selectedColoc || consoSeulement !== false) return;

    const fetchExpenseData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await analyticsService.getMonthlyExpenseAnalytics(selectedColoc.id, currentYear);
        setExpenseData(data);
      } catch (err) {
        setError('Impossible de charger les données de dépenses');
      } finally {
        setLoading(false);
      }
    };

    fetchExpenseData();
  }, [selectedColoc, currentYear, consoSeulement]);

  const handleYearChange = (year: number) => {
    setCurrentYear(year);
  };

  return (
    <div className="tenant-dashboard">
      <h2 className="tenant-dashboard-title">
        Mes charges {selectedColoc ? `— ${selectedColoc.name}` : ''}
      </h2>

      {selectedColoc && consoSeulement ? (
        <div className="tenant-dashboard-content">
          <ConsommationEnergie colocId={selectedColoc.id} depuis={entree} sansMontants />
        </div>
      ) : selectedColoc && consoSeulement === null ? null : selectedColoc ? (
        <div className="tenant-dashboard-content">
          {/* Dit AVANT les chiffres, pas en note de bas de page : une fois le
              montant lu, la nuance arrive trop tard. */}
          <p className="tenant-avertissement">
            <strong>Montants indicatifs.</strong> Ces valeurs ne sont pas
            contractuelles et ne préjugent pas du décompte final des charges
            établi par l’agence. Elles sont là pour vous aider à suivre les
            dépenses de la colocation.
          </p>

          {/* Exactement le composant du propriétaire, sans copie : il ne prend
              qu'un `colocId`, et l'endpoint accepte le locataire — `all_coloc_ids`
              inclut ses `user_colocs`. Ce qu'il voit est donc ce que voit son
              bailleur, aux mêmes règles : rien n'est affiché qui ne soit mesuré.

              Pas de `tenant-chart-section` autour : le composant porte déjà sa
              carte (fond, bordure, padding). L'emballer en produisait deux. */}
          <ConsommationEnergie colocId={selectedColoc.id} depuis={entree} />

          <div className="tenant-chart-section">
            <ExpensesChart
              data={expenseData}
              loading={loading}
              error={error}
              onYearChange={handleYearChange}
              depuis={entree}
            />
          </div>
        </div>
      ) : (
        <div className="tenant-no-coloc">
          <p>Aucune colocation associée à votre compte.</p>
        </div>
      )}
    </div>
  );
};

export default TenantDashboardPage;
